import { Button, Flex, Heading, Text } from '@radix-ui/themes'
import { SideDrawer } from '@Components/common/side-drawer'
import { BasicInformationSection } from './form-sections/basic-information-section'
import { RequirementsSection } from './form-sections/requirements-section'
import { AwardSection } from './form-sections/award-section'
import { GameOption, PromotionCampaignFormData } from './types'
import './styles.scss'

type PromotionCampaignFormDrawerProps = {
  campaign: PromotionCampaignFormData | null
  isCreating: boolean
  isSaving: boolean
  gameOptions: GameOption[]
  pluginMetadataSchema: Record<string, unknown> | null
  awardTypeOptions: Array<{ value: string; label: string }>
  awardTypeOptionsLoading: boolean
  hasStarted: boolean
  onClose: () => void
  onSave: () => void
  onChange: (campaign: PromotionCampaignFormData) => void
}

const isFormValid = (campaign: PromotionCampaignFormData): boolean => {
  // Validate quantity is at least 1
  if (!campaign.triggerCount || campaign.triggerCount < 1) {
    return false
  }

  // Validate that min win amount is not greater than max win amount
  const min = campaign.filters?.winAmountMin
  const max = campaign.filters?.winAmountMax
  if (min !== undefined && max !== undefined && min > max) {
    return false
  }

  return true
}

export const PromotionCampaignFormDrawer = ({
  campaign,
  isCreating,
  isSaving,
  gameOptions,
  pluginMetadataSchema,
  awardTypeOptions,
  awardTypeOptionsLoading,
  hasStarted,
  onClose,
  onSave,
  onChange,
}: PromotionCampaignFormDrawerProps) => {
  if (!campaign) {
    return null
  }

  const formValid = isFormValid(campaign)
  const canEdit = isCreating || !hasStarted

  return (
    <SideDrawer onClose={onClose}>
      <Flex direction="column" gap="4">
        <Heading as="h3" size="4">
          {isCreating ? 'Create Campaign' : 'Edit Campaign'}
        </Heading>

        {!isCreating && hasStarted && (
          <Flex p="3" className="started-promotion-warning">
            <Text size="2" weight="medium" className="warning-text">
              This promotion has started. Only the end date and priority can be modified.
            </Text>
          </Flex>
        )}

        <BasicInformationSection campaign={campaign} canEdit={canEdit} onChange={onChange} />

        <div className="section-separator" />

        <RequirementsSection
          campaign={campaign}
          canEdit={canEdit}
          gameOptions={gameOptions}
          onChange={onChange}
        />

        <div className="section-separator" />

        <AwardSection
          campaign={campaign}
          canEdit={canEdit}
          pluginMetadataSchema={pluginMetadataSchema}
          awardTypeOptions={awardTypeOptions}
          isLoading={awardTypeOptionsLoading}
          onChange={onChange}
        />

        <Flex gap="2" mt="4">
          <Button onClick={onSave} disabled={isSaving || !formValid}>
            {isCreating ? (isSaving ? 'Creating...' : 'Create') : isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </Flex>
    </SideDrawer>
  )
}
